import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    model,
    OnInit,
    Output,
    signal,
    WritableSignal,
} from "@angular/core";
import { TypeManagerDecorator } from "../../../../main/type.map.service";
import { LineItemAdjData } from "../../../../pojo/Pricing";
import { S25Util } from "../../../../util/s25-util";

@TypeManagerDecorator("pricing-table-amount-format")
@Component({
    selector: "pricing-table-amount-format",
    template: `
        @let radioGroupName = itemForUpdate.itemId + "-" + itemForUpdate.itemType + "-" + itemForUpdate.billId;
        <div class="radio-wrapper">
            <s25-ng-radio
                [value]="type()"
                [radioValue]="'amount'"
                [name]="radioGroupName"
                [disabled]="disabled"
                (valueChange)="type.set('amount'); onUpdate()"
                >Amount</s25-ng-radio
            >
            <s25-ng-radio
                [value]="type()"
                [radioValue]="'percent'"
                [name]="radioGroupName"
                [disabled]="disabled"
                (valueChange)="type.set('percent'); onUpdate()"
                >Percent</s25-ng-radio
            >
        </div>
        <div class="input-wrapper">
            @if (type() === "amount") {
                <span class="c-margin-right--quarter">$</span>
            }
            <input
                class="c-input"
                type="number"
                [(ngModel)]="newAdjustment"
                (ngModelChange)="onUpdate()"
                [disabled]="disabled"
            />
            @if (type() === "percent") {
                <span class="c-margin-left--quarter">%</span>
            }
        </div>
        @if (description() || !disabled) {
            <label class="c-margin-top--quarter">
                Description
                <s25-ng-editable-textarea
                    [(val)]="description"
                    (valChange)="onUpdate()"
                    [readOnly]="disabled"
                    [renderHtml]="true"
                ></s25-ng-editable-textarea>
            </label>
        }
        @if (isNewInvoice && !hideApplyAdjButton) {
            <button class="aw-button aw-button--outline" (click)="applyAdjustment()">
                Apply adjustment from {{ itemData.invoiceName }}
            </button>
        }
    `,
    host: {
        "[class.disabled]": "disabled",
    },
    styles: [
        `
            :host {
                max-width: 13em !important;

                .radio-wrapper {
                    display: flex;
                    gap: 5px;
                }

                .input-wrapper {
                    .c-input {
                        width: min(10em, 100%);
                    }

                    & > span {
                        font-size: 1.1rem;
                    }
                }

                label {
                    width: 90%;
                }

                &.disabled {
                    opacity: 0.75;
                    pointer-events: none;
                }
            }
        `,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class PricingTableAmountFormatComponent implements OnInit {
    @Input() itemData: LineItemAdjData;
    @Input() itemDataNewInvoice?: LineItemAdjData;
    @Input() isNewInvoice: boolean;
    oldInvoiceUpdateData = model<Partial<LineItemAdjData>>();
    @Input() disabled: boolean;
    @Input() hideApplyAdjButton: boolean;

    type = signal<"percent" | "amount">("amount");
    newAdjustment = signal(null);
    description = signal("");
    itemForUpdate: LineItemAdjData;

    @Output() updateAdjustment = new EventEmitter<Partial<LineItemAdjData>>();

    ngOnInit() {
        this.itemForUpdate = this.isNewInvoice ? this.itemDataNewInvoice : this.itemData;
        this.newAdjustment.set(this.itemForUpdate?.adjustmentAmt ?? this.itemForUpdate?.adjustmentPercent ?? 0);
        this.description.set(this.itemForUpdate?.adjustmentName ?? "");
        this.type.set(S25Util.isDefined(this.itemForUpdate?.adjustmentPercent) ? "percent" : "amount");
    }

    onAmtUpdate(description: string) {
        const data: Partial<LineItemAdjData> = {
            adjustmentAmt: this.newAdjustment(),
            adjustmentPercent: null,
            adjustmentName: description,
            billId: this.itemForUpdate.billId,
        };
        this.updateAdjustment.emit(data);
        !this.isNewInvoice && this.oldInvoiceUpdateData.set(data);
    }

    onPctUpdate(description: string) {
        const data: Partial<LineItemAdjData> = {
            adjustmentPercent: this.newAdjustment(),
            adjustmentAmt: null,
            adjustmentName: description,
            billId: this.itemForUpdate.billId,
        };
        this.updateAdjustment.emit(data);
        !this.isNewInvoice && this.oldInvoiceUpdateData.set(data);
    }

    onUpdate() {
        const description = this.newAdjustment() === 0 ? "" : this.description();
        if (this.type() === "amount") {
            return this.onAmtUpdate(description);
        }
        return this.onPctUpdate(description);
    }

    applyAdjustment() {
        this.newAdjustment.set(
            this.oldInvoiceUpdateData().adjustmentPercent ?? this.oldInvoiceUpdateData().adjustmentAmt ?? 0,
        );
        this.description.set(this.oldInvoiceUpdateData().adjustmentName ?? "");
        this.type.set(S25Util.isDefined(this.oldInvoiceUpdateData().adjustmentPercent) ? "percent" : "amount");
        this.onUpdate();
    }
}
